import React, { useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  DataGrid,
  Column,
  Paging,
  Pager,
  LoadPanel,
  HeaderFilter,
  FilterRow,
} from 'devextreme-react/data-grid';
import { loadMessages, locale } from 'devextreme/localization';

import './DataGrid.css';

// DevExtreme Türkçe dil desteği
import trMessages from 'devextreme/localization/messages/tr.json';
loadMessages(trMessages);
locale('tr');



const SolmazDataGrid = ({
  title,
  data = [],
  dataSource,
  columns = [],
  height = "auto",
  pageSize = 10,
  allowedPageSizes = [10, 25, 50, "all"],
  keyExpr = "id",
  isLoading = false,
  allowHeaderFiltering = true,
  allowSorting = false,
  filterOnlyDateColumns = true,
  ...props
}) => {
  const dataGridRef = useRef(null);


  const sourceData = Array.isArray(dataSource) ? dataSource : data;
  const totalCount = sourceData.length;
  
  const processedData = useMemo(() => {
    let filteredData = sourceData.map((item, index) => {
      const processedItem = {
        _id: index + 1,
        pk: item && item[keyExpr] ? item[keyExpr] : index + 1,
      };

      if (item && typeof item === "object") {
        Object.keys(item).forEach((key) => {
          const value = item[key];

          if (value === null || value === undefined) {
            processedItem[key] = "";
          } else if (typeof value === "boolean") {
            processedItem[key] = value;
          } else if (typeof value === "object") {
            processedItem[key] = "";
          } else if (typeof value === "number") {
            processedItem[key] = isNaN(value) ? 0 : value;
          } else {
            processedItem[key] = String(value);
          }
        });
      }

      return processedItem;
    });
    
    return filteredData;
  }, [sourceData, keyExpr, columns]);

  const filteredColumns = columns.filter(column => column.visible !== false);

  const restProps = { ...props };
  delete restProps.dataSource;

  return (
    <div className="solmaz-data-grid-container">
      {title && (
        <div className="solmaz-data-grid-header">
          <h2>
            {title} - Toplam {processedData.length} kayıt
          </h2>
        </div>
      )}

      <div className="solmaz-data-grid-wrapper">
        <DataGrid
          ref={dataGridRef}
          dataSource={processedData}
          keyExpr="pk"
          showBorders={true}
          showColumnLines={true}
          showRowLines={true}
          allowColumnReordering={false}
          allowColumnResizing={true}
          columnAutoWidth={true}
          columnMinWidth={50}
          wordWrapEnabled={true}
          height={height}
          width="100%"
          cellHintEnabled={true}
          hoverStateEnabled={true}
          rowAlternationEnabled={true}
          focusedRowEnabled={false}
          selection={{ mode: 'none' }}
          remoteOperations={false}
          showRowHints={true}
          showColumnHints={true}
          onOptionChanged={(e) => {
            if (e.name === 'pageSize') {
              console.log('Page size changed:', e.value);
              console.log('New page count:', Math.ceil(totalCount / e.value));
            }
            if (e.name === 'pageIndex') {
              console.log('Page changed:', e.value);
            }
          }}
          {...restProps}
        >
          <FilterRow 
            visible={true}
            applyFilter="auto"
            showOperationChooser={false}
            showAllText="Tümü"
            resetOperationText="Sıfırla"
            applyFilterText="Uygula"
          />

          <LoadPanel
            text="Veriler yükleniyor..."
            enabled={isLoading}
            showIndicator={true}
          />
          <HeaderFilter 
            visible={allowHeaderFiltering}
            allowSearch={true}
            searchTimeout={500}
            showTooltips={true}
          />

          <Paging 
            enabled={true} 
            defaultPageSize={pageSize}
          />
          
          <Pager
            visible={totalCount > 0}
            allowedPageSizes={allowedPageSizes}
            showPageSizeSelector={true}
            showInfo={true}
            showNavigationButtons={true}
            displayMode="full"
            maxPagesCount={6}
            infoText="Toplam {2} kayıt - Sayfa {0} / {1}"
            pageSizeText="Sayfa başına:"
          />

          <Column dataField="_id" visible={false} />
          <Column dataField="pk" visible={false} />

          {filteredColumns.map((column, index) => {
            const dataField = column.dataField || column.dataIndex;
            const caption = column.caption || column.title;
            const isDateColumn = column.dataType === 'date';
            const shouldAllowFiltering = filterOnlyDateColumns
              ? (isDateColumn && column.allowFiltering !== false)
              : (column.allowFiltering !== false);
            
            const columnFormat = column.format;
            const dateEditorOptions = isDateColumn
              ? {
                  type: 'date',
                  displayFormat: columnFormat || 'dd.MM.yyyy',
                  useMaskBehavior: true,
                  openOnFieldClick: true,
                  showClearButton: true,
                  showDropDownButton: true,
                }
              : undefined;
            const selectedFilterOperation =
              column.selectedFilterOperation || (isDateColumn ? '=' : undefined);
            const filterOperations =
              column.filterOperations || (isDateColumn ? ['=', '>=', '<='] : undefined);
            
            return (
              <Column
                key={`${dataField}-${index}`}
                dataField={dataField}
                caption={caption}
                dataType={column.dataType || "string"}
                format={columnFormat}
                width={column.width}
                minWidth={column.minWidth || 50}
                visible={column.visible !== false}
                allowFiltering={shouldAllowFiltering}
                allowSorting={allowSorting && (column.allowSorting !== false)}
                allowHeaderFiltering={shouldAllowFiltering && (column.allowHeaderFiltering !== false ? allowHeaderFiltering : false)}
                customizeText={column.customizeText}
                fixed={column.fixed}
                fixedPosition={column.fixedPosition}
                alignment={column.alignment || 'center'}
                cellRender={column.cellRender}
                cssClass={`column-${index}`}
                filterType={isDateColumn ? 'date' : undefined}
                editorType={isDateColumn ? 'dxDateBox' : undefined}
                editorOptions={dateEditorOptions}
                selectedFilterOperation={selectedFilterOperation}
                filterOperations={filterOperations}
                cellHintEnabled={true}
                showCellHints={true}
                hint={caption}
              />
            );
          })}
        </DataGrid>
      </div>
    </div>
  );
};

SolmazDataGrid.propTypes = {
  title: PropTypes.string,
  data: PropTypes.array,
  dataSource: PropTypes.array,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      dataField: PropTypes.string,
      dataIndex: PropTypes.string,
      caption: PropTypes.string,
      title: PropTypes.string,
      dataType: PropTypes.string,
      format: PropTypes.string,
      width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      minWidth: PropTypes.number,
      visible: PropTypes.bool,
      customizeText: PropTypes.func,
      fixed: PropTypes.bool,
      fixedPosition: PropTypes.string,
      alignment: PropTypes.string,
      cellRender: PropTypes.func,
      allowFiltering: PropTypes.bool,
      allowSorting: PropTypes.bool,
      allowHeaderFiltering: PropTypes.bool,
      filterOperations: PropTypes.array,
      selectedFilterOperation: PropTypes.string,
    })
  ),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  pageSize: PropTypes.number,
  allowedPageSizes: PropTypes.array,
  keyExpr: PropTypes.string,
  isLoading: PropTypes.bool,
  allowHeaderFiltering: PropTypes.bool,
  allowSorting: PropTypes.bool,
  filterOnlyDateColumns: PropTypes.bool,
};

SolmazDataGrid.defaultProps = {
  data: [],
  dataSource: undefined,
  columns: [],
  height: "auto",
  pageSize: 10,
  allowedPageSizes: [10, 25, 50, "all"],
  keyExpr: "id",
  isLoading: false,
  allowHeaderFiltering: true,
  allowSorting: false,
  filterOnlyDateColumns: false,
};

export default SolmazDataGrid;
